import { Box, Stack } from '@mui/material';
import {
  Button,
  FileInput,
  ImageList,
  SortableList,
  SwitchInput,
  TextInput,
} from 'melp-design/components';
import { FC, useMemo } from 'react';
import {
  Control,
  Controller,
  useFieldArray,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form-latest';
import { useTranslation } from 'react-i18next';
import { useCompanyLanguages, useMe } from 'state/Administrators';
import { useUploadFile } from 'store/files';
import { SurveyType } from 'store/surveys';
import { ContentLanguage } from 'types/general';
import { SystemColors } from 'melp-design/style';
import { Add, Delete } from '@mui/icons-material';
import { Values } from '../../survey-content.types';
import { getInitialValues } from '../../survey-content.constants';

const AnswerTranslationsList: FC<{
  control: Control<Values>;
  questionIndex: number;
  answerIndex: number;
  getValues: UseFormGetValues<Values>;
  surveyType: SurveyType;
  disabled?: boolean;
}> = ({
  control,
  questionIndex,
  answerIndex,
  getValues,
  surveyType,
  disabled,
}) => {
  const { t } = useTranslation();
  const { isFeatureEnabled } = useMe();
  const { defaultLanguage } = useCompanyLanguages();

  const { fields } = useFieldArray({
    name: `questions.${questionIndex}.answers.${answerIndex}.translations`,
    control,
  });

  const selectedLanguage = getValues('ui.selectedLanguage');

  return (
    <>
      {fields.map((field, i) => (
        <Box
          key={field.id}
          sx={{
            display: selectedLanguage === field.language ? 'block' : 'none',
          }}
        >
          <Controller
            name={`questions.${questionIndex}.answers.${answerIndex}.translations.${i}.text`}
            control={control}
            rules={{
              required: {
                value: true,
                message: t('form.required'),
              },
              validate: () =>
                surveyType === 'training'
                  ? getValues(`questions.${questionIndex}.answers`).some(
                      (answer) => answer.isCorrect,
                    ) || t('surveys.answer.isCorrect.error.required')
                  : true,
            }}
            render={({ field: { name, value, onChange, ref }, fieldState }) => {
              const defaultTranslation =
                getValues(
                  `questions.${questionIndex}.answers.${answerIndex}.translations`,
                ).find((t) => t.language === defaultLanguage)?.text ?? '';

              return (
                <TextInput
                  label={t('surveys.optionIndex', {
                    optionNumber: answerIndex + 1,
                  })}
                  name={name}
                  value={value}
                  onChange={onChange}
                  disabled={disabled}
                  emoji
                  rows={1}
                  errorMessage={fieldState.error?.message}
                  infoMessage={
                    defaultLanguage === field.language
                      ? undefined
                      : `${t(
                          'wallet.employeeWalletFundsSource',
                        )} (${defaultLanguage}): ${defaultTranslation}`
                  }
                  sx={{ width: '100%' }}
                  ai={
                    isFeatureEnabled('ai')
                      ? {
                          inputLanguage: defaultLanguage,
                          outputLanguage: field.language,
                          text: defaultTranslation,
                        }
                      : undefined
                  }
                  ref={ref}
                />
              );
            }}
          />
        </Box>
      ))}
    </>
  );
};

export const AnswersList: FC<{
  control: Control<Values>;
  languages: Uppercase<ContentLanguage>[];
  questionIndex: number;
  getValues: UseFormGetValues<Values>;
  setValue: UseFormSetValue<Values>;
  trigger: UseFormTrigger<Values>;
  dragTooltip?: string;
  onPositionChangeCallback?: () => void;
  surveyType: SurveyType;
  disabled?: boolean;
}> = ({
  control,
  languages,
  questionIndex,
  getValues,
  setValue,
  trigger,
  dragTooltip,
  onPositionChangeCallback,
  surveyType,
  disabled,
}) => {
  const { t } = useTranslation();
  const { defaultLanguage } = useCompanyLanguages();
  const { isFeatureEnabled } = useMe();

  const INITIAL_VALUES = useMemo(
    () => getInitialValues(defaultLanguage),
    [defaultLanguage],
  );

  const {
    fields: answerFields,
    append: appendAnswer,
    move: moveAnswers,
    remove: removeAnswer,
  } = useFieldArray({
    name: `questions.${questionIndex}.answers`,
    control,
  });

  const { mutate: uploadFile, isLoading: isUploading } = useUploadFile();

  const answerFormat = getValues(
    `questions.${questionIndex}.choiceAnswerFormat`,
  );

  return (
    <Stack gap={2}>
      <SortableList
        key={JSON.stringify(answerFields.map((field) => field.id))}
        onPositionChange={(source, destination) => {
          moveAnswers(source, destination);
          onPositionChangeCallback?.();
        }}
        dragTooltip={dragTooltip}
        disabled={disabled}
        items={answerFields.map((field, i) => ({
          id: field.id,
          content: (
            <Stack
              direction="row"
              py={1}
              px={2}
              spacing={1}
              sx={{ backgroundColor: SystemColors.white }}
              borderRadius={1}
              boxShadow={` 0 0 0 1px ${SystemColors.grey[90]}`}
              key={field.id}
            >
              <SortableList.DragIndicator />

              <Stack flex={1} gap={1}>
                {answerFormat === 'text' || answerFormat === 'text_image' ? (
                  <AnswerTranslationsList
                    control={control}
                    questionIndex={questionIndex}
                    answerIndex={i}
                    getValues={getValues}
                    surveyType={surveyType}
                    disabled={disabled}
                  />
                ) : null}

                {answerFormat === 'image' || answerFormat === 'text_image' ? (
                  <Controller
                    name={`questions.${questionIndex}.answers.${i}.imageId`}
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t('form.required'),
                      },
                    }}
                    render={({
                      field: { name, value, onChange, ref },
                      fieldState,
                    }) => {
                      const defaultTranslation = getValues(
                        `questions.${questionIndex}.answers.${i}.translations`,
                      ).find((t) => t.language === defaultLanguage);

                      return value ? (
                        <ImageList
                          items={[
                            {
                              imageId: value,
                              actions: [
                                {
                                  icon: Delete,
                                  variant: 'neutral-outline',
                                  disabled,
                                  onClick: () => {
                                    onChange('');
                                  },
                                },
                              ],
                            },
                          ]}
                        />
                      ) : (
                        <FileInput
                          name={name}
                          onChange={([file]) => {
                            if (file) {
                              uploadFile(
                                { file },
                                {
                                  onSuccess: ({ data }) => {
                                    onChange(data.id);
                                  },
                                },
                              );
                            } else {
                              onChange('');
                            }
                          }}
                          disabled={isUploading || disabled}
                          maxFiles={1}
                          previewSelectedFiles={false}
                          accept={['jpg', 'jpeg', 'png', 'gif', 'webp']}
                          error={fieldState.error?.message}
                          ai={
                            isFeatureEnabled('ai')
                              ? {
                                  title: defaultTranslation?.text ?? '',
                                  aspectRatio: [16, 9],
                                }
                              : undefined
                          }
                          ref={ref}
                        />
                      );
                    }}
                  />
                ) : null}
              </Stack>

              {surveyType === 'training' ? (
                <Box
                  flexShrink={0}
                  sx={{ height: 40, display: 'flex', alignItems: 'center' }}
                >
                  <Controller
                    name={`questions.${questionIndex}.answers.${i}.isCorrect`}
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => {
                      const questionType = getValues(
                        `questions.${questionIndex}.type`,
                      );

                      return (
                        <SwitchInput
                          title={t('surveys.answer.isCorrect.label')}
                          name={name}
                          value={value}
                          disabled={disabled}
                          appearance={
                            questionType === 'single_choice_response'
                              ? 'radio'
                              : questionType === 'multiple_choice_response'
                              ? 'checkbox'
                              : undefined
                          }
                          onChange={(v) => {
                            if (
                              questionType === 'single_choice_response' &&
                              v
                            ) {
                              setValue(
                                `questions.${questionIndex}.answers`,
                                getValues(
                                  `questions.${questionIndex}.answers`,
                                ).map((answer, ai) => ({
                                  ...answer,
                                  isCorrect: ai === i,
                                })),
                                { shouldDirty: true },
                              );
                            } else {
                              onChange(v);
                            }

                            trigger(`questions.${questionIndex}.answers`);
                          }}
                          ref={ref}
                        />
                      );
                    }}
                  />
                </Box>
              ) : null}

              <Box flexShrink={0}>
                <Button
                  variant="neutral-outline"
                  size="md"
                  icon={Delete}
                  onClick={() => removeAnswer(i)}
                  title={t('common.remove')}
                  disabled={disabled}
                />
              </Box>
            </Stack>
          ),
        }))}
      />

      <Box>
        <Button
          label={t('surveys.addAnswerOption')}
          variant="tertiary"
          icon={Add}
          disabled={disabled}
          onClick={() => {
            appendAnswer({
              ...INITIAL_VALUES.questions[0].answers[0],
              translations: languages.map((language) => ({
                ...INITIAL_VALUES.questions[0].answers[0].translations[0],
                language,
              })),
            });
          }}
        />
      </Box>
    </Stack>
  );
};
